/**
 * The hologram figure (D169): rooms as lit volumes on stacked floor plates,
 * corridors under them, lifts between floors, one glowing material, and a
 * legend that explains only what is drawn.
 *
 * @remarks
 * It draws the engine's POSITIONED PRIMITIVES (`HoloMapLayout`) as native
 * JSX and computes no geometry — every polygon arrives as a `points` string
 * from `buildMapHoloLayout`, in painter's order, so this file draws in
 * sequence and is right. What it decides is the MATERIAL: one custom
 * property (`--orpheus-holo`) that the pack's tint sets on the figure and
 * the stylesheet spends on edges, fills and the glow filter; and the
 * knowledge tiers as classes, so fog is a stroke style as well as a color.
 *
 * Accessibility, as the flat map's: `role="img"` with a title and the
 * engine's description (which now counts the floors), room names as real
 * `<text>` nodes, and the legend as an ordinary list under the figure.
 * Where a name sits is the engine's decision too (D174): on the face,
 * beside the box, or above it on a leader — this file draws the anchor
 * and the line it is handed.
 *
 * @packageDocumentation
 */

import { type MapHoloLegendKey, type MapHoloNodeLabel, type MapLayout, SMALL_LABEL_SCALE } from '@orpheus/engine-runtime';
import { type CSSProperties } from 'react';

/** The hologram's layout — the engine's projected primitives, tagged `holo`, with the pack's tint (D209: laid out where it is drawn, no longer on the wire). */
export type HoloMapLayout = Extract<MapLayout, { readonly style: 'holo' }>;

/** What the legend reads off a layout: the keys it must explain and the material they wear — the hologram's or the scene's (D210). */
export type LegendSource = Pick<HoloMapLayout, 'legend' | 'tint'>;

/** Props for {@link HoloMapFigure}. */
export interface HoloMapFigureProps {
	readonly layout: HoloMapLayout;
	/** The figure's accessible name — what the pack calls its map (D189). */
	readonly title: string;
}

/** The hologram's label size, as `.orpheus-holo__label` sets it in the stylesheet (D174: 10px). */
const HOLO_LABEL_FONT_SIZE = 10;
/** The small label's size, the engine's ratio of the base (D224: 8px) — what the markup carries for a page without the stylesheet. */
const SMALL_HOLO_LABEL_FONT_SIZE = HOLO_LABEL_FONT_SIZE * SMALL_LABEL_SCALE;

/** What each legend entry says — the shell's words, in the chrome's register. */
export const HOLO_LEGEND_LABELS: Readonly<Record<MapHoloLegendKey, string>> = {
	active: 'You are here',
	/*
	 * D169 (Calder): "Checkpoint" and not, because the word names a MECHANISM
	 * the legend cannot explain and a pack may already have spent. Blacksite
	 * has a room called the Checkpoint — a security post — and it is not the
	 * checkpoint: `isCheckpoint` is on the Reactor Substation. A player who
	 * reads CHECKPOINT in the legend, finds the room named Checkpoint
	 * unmarked, and finds the mark on a room called Substation 3 concludes
	 * the map is broken. "Restart point" also says what the white edge DOES,
	 * which "Checkpoint" only says to someone who already knew.
	 */
	checkpoint: 'Restart point',
	/*
	 * D169 (Calder): "Stairs or lift" named two mechanisms, and the POC has
	 * NEITHER — its verticals are a ladder of staples down the drop and a
	 * hole through the muster room's floor grille. A legend that names the
	 * wrong mechanism is worse than one that names none, because the player
	 * has already read the prose that says ladder. "Between floors" is what
	 * the dotted vertical literally is, it is true of stairs, lifts, ladders,
	 * grilles and shafts alike, and it pairs with the LEVEL n plates the
	 * player is looking at while they read it.
	 */
	lift: 'Between floors',
	revealed: 'Known, not yet walked',
	unknown: 'Unexplored',
	visited: 'Walked',
};

/** The class list for one node's box. */
const nodeClassName = (node: HoloMapLayout['nodes'][number]): string =>
	[
		'orpheus-holo__node',
		`orpheus-holo__node--${node.knowledge}`,
		`orpheus-holo__node--${node.kind}`,
		...(node.isActive ? ['orpheus-holo__node--active'] : []),
		...(node.isCheckpoint ? ['orpheus-holo__node--checkpoint'] : []),
	].join(' ');

/** Props for {@link HoloMapLegend}. */
export interface HoloMapLegendProps {
	readonly layout: LegendSource;
}

/**
 * The material the figure and its legend share: the pack's tint as one
 * custom property, or nothing — the stylesheet's default is the brand's
 * signal green. Set on both roots rather than on a wrapper, so the canvas's
 * layout has nothing new to know about.
 */
const materialOf = (layout: LegendSource): CSSProperties | undefined =>
	layout.tint === undefined ? undefined : ({ '--orpheus-holo': layout.tint } as CSSProperties);

/**
 * Render the hologram: the figure alone, so the panel can place its legend
 * beneath the canvas rather than beside the drawing.
 *
 * @param props - {@link HoloMapFigureProps}
 * @returns The figure
 */
export const HoloMapFigure = ({ layout, title }: HoloMapFigureProps) => (
	<svg
		aria-labelledby="map-panel-svg-title map-panel-svg-desc"
		className="orpheus-holo map-panel__figure"
		height={layout.height}
		role="img"
		style={materialOf(layout)}
		viewBox={`0 0 ${String(layout.width)} ${String(layout.height)}`}
		width={layout.width}
	>
		<title id="map-panel-svg-title">{title}</title>
		<desc id="map-panel-svg-desc">{layout.description}</desc>
		<defs>
			{/* The glow: the material blurred under itself. One filter, referenced by class, so a pack's tint glows in its own color. */}
			<filter height="140%" id="orpheus-holo-glow" width="140%" x="-20%" y="-20%">
				<feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="2.2" />
				<feMerge>
					<feMergeNode in="blur" />
					<feMergeNode in="SourceGraphic" />
				</feMerge>
			</filter>
		</defs>
		{layout.floors.map((floor) => (
			<g className="orpheus-holo__floor" key={`floor-${String(floor.level)}`}>
				<polygon className="orpheus-holo__plate" points={floor.points} />
				<text className="orpheus-holo__floor-label" textAnchor="start" x={floor.labelX} y={floor.labelY}>
					{floor.label}
				</text>
			</g>
		))}
		{layout.corridors.map((corridor) => (
			<polygon className="orpheus-holo__corridor" key={`corridor-${corridor.points}`} points={corridor.points} />
		))}
		{layout.nodes.map((node) => (
			<g className={nodeClassName(node)} key={`node-${node.key}`}>
				<polygon className="orpheus-holo__face orpheus-holo__face--left" points={node.left} />
				<polygon className="orpheus-holo__face orpheus-holo__face--right" points={node.right} />
				<polygon className="orpheus-holo__face orpheus-holo__face--top" points={node.top} />
				{/* D174: a label that floats above its box hangs from a leader; one beside or on the box needs none.
				    D224: a stub on a narrow figure has no label at all — the box is drawn and not named. */}
				{node.label?.leader === undefined ? undefined : (
					<line className="orpheus-holo__leader" x1={node.label.leader.x1} x2={node.label.leader.x2} y1={node.label.leader.y1} y2={node.label.leader.y2} />
				)}
				{node.label === undefined ? undefined : <HoloMapLabel label={node.label} />}
			</g>
		))}
		{layout.lifts.map((lift) => (
			<line
				className="orpheus-holo__lift"
				key={`lift-${String(lift.x1)}-${String(lift.y1)}-${String(lift.y2)}`}
				x1={lift.x1}
				x2={lift.x2}
				y1={lift.y1}
				y2={lift.y2}
			/>
		))}
	</svg>
);

/**
 * One room's name as the layout placed it. A name set a size down (D224:
 * a walked room the player is not in, on a narrow figure) carries the
 * modifier the stylesheet sizes, and the size as a presentation attribute
 * for a page without it; the hologram's own size is the stylesheet's, so a
 * wide figure's markup is what it was.
 *
 * @param props - The placed label
 * @returns The text
 */
const HoloMapLabel = ({ label }: { readonly label: MapHoloNodeLabel }) =>
	label.size === 'small' ? (
		<text className="orpheus-holo__label orpheus-holo__label--small" fontSize={SMALL_HOLO_LABEL_FONT_SIZE} textAnchor={label.anchor} x={label.x} y={label.y}>
			{label.text}
		</text>
	) : (
		<text className="orpheus-holo__label" textAnchor={label.anchor} x={label.x} y={label.y}>
			{label.text}
		</text>
	);

/**
 * Render the legend: only the entries the layout says are drawn, in the
 * shell's words, wearing the figure's material.
 *
 * @param props - {@link HoloMapLegendProps}
 * @returns The legend list
 */
export const HoloMapLegend = ({ layout }: HoloMapLegendProps) => (
	<ul aria-label="Map legend" className="map-panel__legend" style={materialOf(layout)}>
		{layout.legend.map((key) => (
			<li className={`map-panel__legend-entry map-panel__legend-entry--${key}`} key={key}>
				<span aria-hidden="true" className="map-panel__legend-swatch" />
				{HOLO_LEGEND_LABELS[key]}
			</li>
		))}
	</ul>
);
